<template>
  <div
    ref="phoneRoot"
    class="vue-phone-number-input"
    :class="[themeModifierClass, validationStateClass, sizeModifierClass, { 'vue-phone-number-input--has-value': hasPhoneValue }]"
  >
    <label v-if="countryCodeLabel" class="vue-phone-number-input__country-label">
      {{ countryCodeLabel }}
    </label>

    <label
      v-if="phoneNumberLabelText"
      class="vue-phone-number-input__phone-label"
      :for="$attrs.id"
      @click="focusInput"
    >
      {{ phoneNumberLabelText }}
    </label>

    <VueTelInput
      :model-value="modelValue"
      :default-country="(defaultCountryCode || 'NL').toLowerCase()"
      mode="national"
      valid-characters-only
      :dropdown-options="dropdownOptions"
      :input-options="inputOptions"
      @on-input="onInput"
      @validate="onValidate"
    />
  </div>
</template>

<script>
import { VueTelInput } from 'vue-tel-input'
import { getExampleNumber } from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'
import { applyElementStyles } from '../../utils/runtimeThemeSheet'
import 'vue-tel-input/vue-tel-input.css'

// vue-tel-input does not merge these; a partial object replaces library defaults.
const DEFAULT_DROPDOWN_OPTIONS = {
  ariaLabel: '',
  disabled: false,
  showDialCodeInList: true,
  showDialCodeInSelection: false,
  showFlags: true,
  showSearchBox: false,
  searchBoxPlaceholder: '',
  tabindex: 0
}

const DEFAULT_INPUT_OPTIONS = {
  autocomplete: 'tel',
  autofocus: false,
  maxlength: 25,
  name: 'telephone',
  showDialCode: false,
  readonly: false,
  required: false,
  tabindex: 0,
  type: 'tel',
  inputmode: 'numeric'
}

const THEME_PRIMARY = '#073c58'
const MEEK_PRIMARY = '#ee3400'

export default {
  name: 'VuePhoneNumberInput',
  inheritAttrs: false,
  components: { VueTelInput },
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    defaultCountryCode: {
      type: String,
      default: 'NL'
    },
    size: String,
    color: String,
    errorColor: String,
    validColor: String,
    translations: Object,
    isValid: {
      type: Boolean,
      default: null
    },
    error: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'update'],
  data () {
    return {
      results: null,
      isPhoneValid: false,
      hasPhoneValue: false,
      selectedCountryCode: this.defaultCountryCode || 'NL'
    }
  },
  computed: {
    themePrimary () {
      if (this.color) {
        return this.color
      }
      return this.$store?.getters?.isMeekijken ? MEEK_PRIMARY : THEME_PRIMARY
    },
    themeModifierClass () {
      if (this.color) return 'vue-phone-number-input--custom'
      return this.$store?.getters?.isMeekijken ? 'vue-phone-number-input--meek' : 'vue-phone-number-input--wsd'
    },
    resolvedValidColor () {
      return this.validColor || '#00E64B'
    },
    resolvedErrorColor () {
      return this.errorColor || '#dc3545'
    },
    resolvedIsValid () {
      if (typeof this.isValid === 'boolean') {
        return this.isValid
      }
      return this.isPhoneValid
    },
    validationStateClass () {
      return {
        'vue-phone-number-input--valid': this.resolvedIsValid && this.hasPhoneValue && !this.error,
        'vue-phone-number-input--error': this.error
      }
    },
    countryCodeLabel () {
      return this.translations?.countrySelectorLabel || ''
    },
    phoneNumberLabelText () {
      const exampleWord = this.translations?.example
      const exampleNumber = this.phoneNumberExample
      if (exampleWord && exampleNumber) {
        const label = `${exampleWord} ${exampleNumber}`
        return this.isRequired ? `${label} *` : label
      }

      const fallback = this.placeholder || this.translations?.phoneNumberLabel || ''
      return this.isRequired && fallback ? `${fallback} *` : fallback
    },
    phoneNumberExample () {
      const countryCode = (this.selectedCountryCode || this.defaultCountryCode || 'NL').toUpperCase()
      try {
        const example = getExampleNumber(countryCode, examples)
        return example ? example.formatNational().replace(/\s/g, '') : '0612345678'
      } catch {
        return '0612345678'
      }
    },
    isRequired () {
      const required = this.$attrs.required
      return required === true || required === '' || required === 'required'
    },
    sizeModifierClass () {
      if (this.size === 'lg') return 'vue-phone-number-input--lg'
      if (this.size === 'sm') return 'vue-phone-number-input--sm'
      return null
    },
    dropdownOptions () {
      return {
        ...DEFAULT_DROPDOWN_OPTIONS,
        ariaLabel: this.countryCodeLabel || DEFAULT_DROPDOWN_OPTIONS.ariaLabel,
        showDialCodeInSelection: true,
        showDialCodeInList: true,
        showFlags: true
      }
    },
    inputOptions () {
      const classes = ['form-control']
      if (this.size === 'lg') classes.push('form-control-lg')
      if (this.size === 'sm') classes.push('form-control-sm')

      return {
        ...DEFAULT_INPUT_OPTIONS,
        id: this.$attrs.id,
        required: this.isRequired,
        placeholder: this.phoneNumberLabelText || undefined,
        styleClasses: classes
      }
    }
  },
  watch: {
    themePrimary () {
      this.syncThemeVars()
    },
    validColor () {
      this.syncThemeVars()
    },
    errorColor () {
      this.syncThemeVars()
    },
    modelValue () {
      this.hasPhoneValue = Boolean(String(this.modelValue || '').trim())
      if (!this.modelValue) {
        this.isPhoneValid = false
      }
    }
  },

  mounted () {
    this.syncThemeVars()
    this.hasPhoneValue = Boolean(String(this.modelValue || '').trim())
  },

  methods: {
    syncThemeVars () {
      const styles = {
        '--vpi-valid-color': this.resolvedValidColor,
        '--vpi-error-color': this.resolvedErrorColor
      }
      if (this.color) {
        styles['--vti-primary'] = this.themePrimary
        styles['--vti-border-default'] = '#ced4da'
      }
      applyElementStyles(this.$refs.phoneRoot, styles)
    },
    updateValidationState (phoneObject) {
      if (!phoneObject) {
        this.isPhoneValid = false
        this.hasPhoneValue = Boolean(String(this.modelValue || '').trim())
        return
      }

      if (phoneObject.country?.iso2) {
        this.selectedCountryCode = phoneObject.country.iso2.toUpperCase()
      }

      const number = phoneObject.number || phoneObject.nationalNumber || this.modelValue
      this.hasPhoneValue = Boolean(String(number || '').trim())
      this.isPhoneValid = phoneObject.valid === true
    },
    onValidate (phoneObject) {
      this.updateValidationState(phoneObject)
    },
    focusInput () {
      // vue-tel-input exposes no stable ref for the inner input; query the rendered DOM.
      this.$nextTick(() => {
        this.$el?.querySelector('input.vti__phone')?.focus()
      })
    },
    onInput (number, phoneObject) {
      this.$emit('update:modelValue', number)
      this.updateValidationState(phoneObject)
      if (!phoneObject) {
        return
      }
      const payload = {
        formattedNumber: phoneObject.formatted,
        e164: phoneObject.number,
        nationalNumber: phoneObject.nationalNumber,
        countryCode: phoneObject.country?.iso2,
        isValid: phoneObject.valid === true,
        phoneNumber: phoneObject
      }

      this.results = payload
      this.$emit('update', payload)
    }
  }
}
</script>

<style scoped>
.vue-phone-number-input {
  position: relative;
  font-size: 14px;
  --vti-border-default: #ced4da;
  --vpi-valid-color: #00E64B;
  --vpi-error-color: #dc3545;
}

.vue-phone-number-input--wsd {
  --vti-primary: #073c58;
}

.vue-phone-number-input--meek {
  --vti-primary: #ee3400;
}

.vue-phone-number-input__country-label {
  position: absolute;
  top: 0.3rem;
  left: 0.3rem;
  z-index: 2;
  pointer-events: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  padding: 0 0.35rem;
}

.vue-phone-number-input__phone-label {
  position: absolute;
  top: 4px;
  left: calc(7.5rem + 12px);
  right: 12px;
  z-index: 2;
  cursor: pointer;
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
  color: #747474;
  opacity: 0;
  transform: translateY(25%);
  transition: all 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vue-phone-number-input--has-value .vue-phone-number-input__phone-label {
  opacity: 1;
  transform: translateY(0);
}

.vue-phone-number-input:focus-within .vue-phone-number-input__phone-label {
  color: var(--vti-primary);
}

.vue-phone-number-input--valid.vue-phone-number-input--has-value .vue-phone-number-input__phone-label {
  color: var(--vpi-valid-color);
}

.vue-phone-number-input--error.vue-phone-number-input--has-value .vue-phone-number-input__phone-label {
  color: var(--vpi-error-color);
}

.vue-phone-number-input--has-value :deep(.vti__input.vti__phone) {
  padding-top: 14px;
}

.vue-phone-number-input--lg.vue-phone-number-input--has-value :deep(.vti__input.vti__phone) {
  padding-top: 16px;
}

.vue-phone-number-input--lg .vue-phone-number-input__phone-label {
  font-size: 12px;
}

.vue-phone-number-input--sm.vue-phone-number-input--has-value :deep(.vti__input.vti__phone) {
  padding-top: 12px;
}

.vue-phone-number-input--sm .vue-phone-number-input__phone-label {
  font-size: 10px;
}

.vue-phone-number-input :deep(.vue-tel-input) {
  position: relative;
  border-radius: 4px !important;
  border: 1px solid var(--vti-border-default) !important;
  font-size: 14px;
  /* Paint the whole control white so the country selector matches the number field and
     the two read as one input (vue-tel-input leaves the selector transparent by default). */
  background-color: #fff;
}

.vue-phone-number-input :deep(.vti__dropdown) {
  position: static;
  min-width: 7.5rem;
  width: 7.5rem;
  border-right: 1px solid var(--vti-border-default);
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  font-size: 14px;
}

.vue-phone-number-input :deep(.vti__selection) {
  font-size: 14px;
}

.vue-phone-number-input :deep(.vti__selection .vti__country-code) {
  font-size: 14px;
}

/* Anchor country list to the bottom of the full field, not the selector button */
.vue-phone-number-input :deep(.vti__dropdown-list) {
  left: 0;
  width: 100%;
}

.vue-phone-number-input :deep(.vti__dropdown-list.below) {
  top: 100%;
}

.vue-phone-number-input :deep(.vti__dropdown-list.above) {
  top: auto;
  bottom: 100%;
}

.vue-phone-number-input :deep(.vue-tel-input:hover) {
  border-color: var(--vti-primary) !important;
}

.vue-phone-number-input :deep(.vue-tel-input:focus-within) {
  border-color: var(--vti-primary) !important;
  box-shadow: none !important;
}

.vue-phone-number-input--valid :deep(.vue-tel-input) {
  border-color: var(--vpi-valid-color) !important;
}

.vue-phone-number-input--valid :deep(.vue-tel-input .vti__dropdown) {
  border-right-color: var(--vpi-valid-color) !important;
}

.vue-phone-number-input--valid :deep(.vue-tel-input:hover),
.vue-phone-number-input--valid :deep(.vue-tel-input:focus-within) {
  border-color: var(--vpi-valid-color) !important;
  box-shadow: none !important;
}

.vue-phone-number-input--valid :deep(.vue-tel-input:hover .vti__dropdown),
.vue-phone-number-input--valid :deep(.vue-tel-input:focus-within .vti__dropdown) {
  border-right-color: var(--vpi-valid-color) !important;
}

.vue-phone-number-input--error :deep(.vue-tel-input) {
  border-color: var(--vpi-error-color) !important;
}

.vue-phone-number-input--error :deep(.vue-tel-input .vti__dropdown) {
  border-right-color: var(--vpi-error-color) !important;
}

.vue-phone-number-input--error :deep(.vue-tel-input:hover),
.vue-phone-number-input--error :deep(.vue-tel-input:focus-within) {
  border-color: var(--vpi-error-color) !important;
  box-shadow: none !important;
}

.vue-phone-number-input--error :deep(.vue-tel-input:hover .vti__dropdown),
.vue-phone-number-input--error :deep(.vue-tel-input:focus-within .vti__dropdown) {
  border-right-color: var(--vpi-error-color) !important;
}

.vue-phone-number-input :deep(.vue-tel-input:hover .vti__dropdown),
.vue-phone-number-input :deep(.vue-tel-input:focus-within .vti__dropdown) {
  border-right-color: var(--vti-primary) !important;
}

.vue-phone-number-input :deep(.vti__input.vti__phone) {
  caret-color: var(--vti-primary);
  border: none !important;
  box-shadow: none !important;
  outline: none;
  border-top-right-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  font-size: 14px;
}

.vue-phone-number-input :deep(.vti__input.vti__phone:focus) {
  border: none !important;
  box-shadow: none !important;
  outline: none;
}

.vue-phone-number-input :deep(.vti__dropdown-item) {
  font-size: 14px;
}

/* Visual alignment: shift flag + dial code down slightly */
.vue-phone-number-input :deep(.vti__dropdown .vti__flag),
.vue-phone-number-input :deep(.vti__dropdown .vti__country-code),
.vue-phone-number-input :deep(.vti__dropdown .vti__dropdown-arrow) {
  transform: translateY(2px);
}

/* Hide arrow (dropdown affordance handled by clicking the country selector) */
.vue-phone-number-input :deep(.vti__dropdown-arrow) {
  display: none;
}

/* More spacing between the flag and the dial code (+31) */
.vue-phone-number-input :deep(.vti__selection .vti__flag) {
  margin-right: 10px;
}

/* NL flag spacing */
.vue-phone-number-input :deep(.vti__flag.nl) {
  margin-left: 3px;
}
</style>
